/*
 *
 * LanguageProvider
 *
 * This component connects the Redux language state (locale) to
 * IntlProvider and messages (loaded from `src/translations`
 * or from external files on CMS)
 *
 * The default dictionary is 'nexibusiness' (nb).
 * All other banks (uci, future) will take this dictionary
 * and can integrate or modify their specific keys.
 * Supported NB languages: 'it', 'en', 'fr', 'es', 'de', 'el'.
 * UCI local dictionaries currently override NB copy for 'it' and 'en'.
 */

import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { IntlProvider } from 'react-intl'
import { getLanguages } from 'Utility/getDataFromCMS'
import PortalLoader from '@nexidigital/portalloader'
import moment from 'moment'
import 'moment/locale/it'
import { merge } from 'lodash'
import { initIntl } from 'Utility/intlIstance'
import itUciStrings from 'Translations/uci/lang-it.json'
import enUciStrings from 'Translations/uci/lang-en.json'
import itNBStrings from 'Translations/nb/lang-it.json'
import enNBStrings from 'Translations/nb/lang-en.json'
import deNBStrings from 'Translations/nb/lang-de.json'
import esNBStrings from 'Translations/nb/lang-es.json'
import frNBStrings from 'Translations/nb/lang-fr.json'
import elNBStrings from 'Translations/nb/lang-el.json'

export function LanguageProvider (props) {
  const [translationMessages, setTranslationMessage] = useState(null)

  const bank = process.env.BANK.toLowerCase()

  const userLanguage =
    window.localStorage.getItem('userLanguage') ||
    navigator.language ||
    navigator.userLanguage

  // Default local dictionaries by bank and locale.
  const defaultDictionaries = {
    nexibusiness: {
      it: { ...itNBStrings },
      en: { ...enNBStrings },
      de: { ...deNBStrings },
      es: { ...esNBStrings },
      fr: { ...frNBStrings },
      el: { ...elNBStrings }
    },
    uci: {
      it: merge({ ...itNBStrings }, { ...itUciStrings }),
      en: merge({ ...enNBStrings }, { ...enUciStrings })
    }
    // add other banks here
  }

  // determination of the locale to use
  let locale = ''
  if (props.language) {
    locale = props.language
  } else if (userLanguage.startsWith('it')) {
    locale = 'it'
  } else {
    locale = 'en'
  }
  moment.locale(locale)

  // load dictionaries (from CMS or default)
  const asyncLanguages = async (loc) => {
    if (String(process.env.LANGUAGE_ON_CMS) === 'true') {
      try {
        const bankDictionary = await getLanguages(loc)
        if (bank === 'nexibusiness') {
          return bankDictionary
        }
        const generalDictionary = await getLanguages(loc)
        return merge({ ...generalDictionary }, { ...bankDictionary })
      } catch {
        return defaultDictionaries[bank] || defaultDictionaries.nexibusiness
      }
    } else {
      return defaultDictionaries[bank] || defaultDictionaries.nexibusiness
    }
  }

  useEffect(() => {
    asyncLanguages(locale).then((res) => {
      const messages = { ...res }
      const supportedLocale = messages[locale] ? locale : 'en'
      Object.keys(messages[supportedLocale]).forEach((key) => {
        const msg = messages[supportedLocale][key]
        messages[supportedLocale][key] = msg && msg.replace(/<br\s*\/?>/gi, '{br}')
      })
      initIntl(supportedLocale, messages[supportedLocale])
      setTranslationMessage(messages)
    })
  }, [locale])

  return translationMessages
    ? (
      <IntlProvider
        locale={locale}
        messages={
        String(process.env.LANGUAGE_ON_CMS) === 'true'
          ? translationMessages
          : translationMessages[locale] || translationMessages.en
      }
      >
        {React.Children.only(props.children)}
      </IntlProvider>
      )
    : (
      <PortalLoader circular centered />
      )
}

LanguageProvider.propTypes = {
  language: PropTypes.string,
  children: PropTypes.element.isRequired
}

LanguageProvider.defaultProps = {
  language: ''
}

export default LanguageProvider
